import { ApolloError } from '@apollo/client';
import { parse } from 'cookie';
import { Request } from 'express';
import { GraphQLFormattedError } from 'graphql';
import fetch from 'isomorphic-fetch';
import { action, observable } from 'mobx';

import { CSRF_FIELD_NAME, USER_COOKIE_NAME } from '../chromatic-lib/constants/Authentication';
import { clearRedirectAfterAuth } from './redirectAfterAuth';
import { clearLastViewedAppCookie } from './withLastViewedAppCookie';

// If we are running in the browser, there is only every going to be a singleton token

function loginTokenFromCookie(cookie: string) {
  return parse(cookie)[USER_COOKIE_NAME];
}

type TokenSingleton = { isLoggedIn: boolean };
let tokenSingleton: TokenSingleton;

const tokenSingletonFromRequest = (req: Request): TokenSingleton => ({
  isLoggedIn: !!loginTokenFromCookie(req.headers.cookie || ''),
});

// No need to be observable on the server
export const getTokenSingleton = ({ req }: { req?: Request } = {}) =>
  req ? tokenSingletonFromRequest(req) : tokenSingleton;

let popup: Window;
const openPopup = ({ title, width, height }) => {
  const left = (window.innerWidth - width) / 2 + window.screenLeft;
  const top = (window.innerHeight - height) / 2 + window.screenTop;
  const options = `scrollbars=yes,width=${width},height=${height},top=${top},left=${left}`;
  popup = window.open('about:blank', title, options);

  if (!popup) return false;

  if (window.focus) popup.focus();
  return true;
};

let doLoginWithProvider: (value?: unknown) => void;

const POPUP_SIZE = 800;
const FORM_ID = 'oauth-connect-initiator';

export const loginWithProvider = async ({ provider, subdomain }) => {
  // Retrieve a CSRF token. This also sets a CSRF cookie with the same value.
  const { csrfToken } = await fetch('/csrf', {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  }).then((res: Response) => res.json());

  // Construct a form to trigger a POST request.
  // TODO: never use a popup -- rip this and related code out in follow up PR
  // https://linear.app/chromaui/issue/AP-7334/remove-logic-to-show-login-in-popup
  const usePopup = false;
  let target = '_self';

  if (usePopup && openPopup({ title: 'oauth-dialog', width: POPUP_SIZE, height: POPUP_SIZE })) {
    target = 'oauth-dialog';
  }

  // If there is an existing form from a previous attempt, remove it
  document.getElementById(FORM_ID)?.remove();

  document.body.insertAdjacentHTML(
    'beforeend',
    `<form action="/connect/${provider}?subdomain=${subdomain}" id="${FORM_ID}" method="post" target="${target}" style="display: none">
      <input type="hidden" name="${CSRF_FIELD_NAME}" value="${csrfToken}" />
    </form>`
  );

  // Automatically submit the form
  const form = document.getElementById(FORM_ID) as HTMLFormElement;
  form.submit();

  // This promise might never return if the user doesn't end up following through with the process.
  return new Promise<void>((r) => {
    doLoginWithProvider = r;
  });
};

if (typeof window !== 'undefined') {
  /* eslint-env browser */
  tokenSingleton = observable({
    isLoggedIn: !!loginTokenFromCookie(document.cookie),
  });

  // Could potentially export this
  const setLoggedIn = action(() => {
    tokenSingleton.isLoggedIn = true;
  });

  const handleMessage = ({ origin, data }) => {
    if (origin !== location.origin) {
      // Funny/buggers
      return;
    }

    const { message } = data;
    if (message === 'login') {
      setLoggedIn();

      if (doLoginWithProvider) {
        doLoginWithProvider();
      }

      popup?.close();
    }
  };

  window.addEventListener('message', handleMessage, false);
}

export const logout = action(() => {
  // Cookies are unset via the response headers, and tokens are invalidated (blacklisted).
  // This method only works on the client side as written
  // fetch requires an absolute URL in node and clearLastViewedAppCookie uses browser cookie APIs
  // We may be able change that by using an absolute URL and unsetting cookies in
  // `logout-handler.ts` in the index, unsure if a fetch in SSR would unset the cookies though.
  // clearRedirectAfterAuth uses browser sessionStorage APIs but catches any errors
  if (typeof window !== 'undefined') {
    fetch('/logout', { method: 'POST' }).then((response: Response) => response.text()); // Response is unused but not reading the body is a memory leak
    clearLastViewedAppCookie();
    clearRedirectAfterAuth();
    tokenSingleton.isLoggedIn = false;
  }
});

const isApolloError = (error?: Error | ApolloError | GraphQLFormattedError): error is ApolloError =>
  error instanceof ApolloError;

const isGraphQLFormattedError = (
  error?: Error | ApolloError | GraphQLFormattedError
): error is GraphQLFormattedError => !!(error as GraphQLFormattedError)?.extensions;

export const isMustLoginError = (error?: Error | ApolloError | GraphQLFormattedError) =>
  !!error?.message.match(/Must login/) ||
  !!(isApolloError(error) && error?.graphQLErrors?.some((error) => isMustLoginError(error))) ||
  !!(isGraphQLFormattedError(error) && error?.extensions?.mustLogin);
